import api from "./api";

// Helper to convert frontend type to backend type
const getBackendType = (displayType) => {
  if (!displayType) {
    console.error('Received undefined or null displayType');
    return 'text'; // Default to text if type is missing
  }

  const typeMap = {
    'Text': 'text',
    'Multiple Choice': 'multiple_choice',
    'Checkbox': 'checkbox',
    'Rating Scale': 'rating',
    'Likert Scale': 'likert',
    'Dropdown': 'dropdown',
    'Yes/No': 'yes_no',
    'Numeric': 'numeric',
    'Date': 'date',
    'Email': 'email'
  };

  const backendType = typeMap[displayType];
  if (!backendType) {
    console.warn(`Unknown question type: ${displayType}, using lowercase version`);
  }
  return backendType || displayType.toLowerCase();
};

// Helper to format question options based on type
const formatQuestionOptions = (type, options) => {
  if (!options) return null;

  switch (type) {
    case 'likert':
      return options;
    case 'rating':
      return {
        min_value: 1,
        max_value: 5,
        step: 1,
        ...options
      };
    case 'numeric':
      return {
        min_value: options.min_value || null,
        max_value: options.max_value || null,
        step: options.step || 1
      };
    default:
      return options;
  }
};

const surveyService = {
  getAllSurveys: async () => {
    const response = await api.get('/api/surveys');
    return response.data;
  },

  getSurveysForStudy: async (studyID) => {
    const response = await api.get(`/api/studies/${studyID}/surveys`);
    return response.data;
  },

  createSurvey: async (surveyData) => {
    try {
      console.log('Received survey data:', JSON.stringify(surveyData, null, 2));

      // Make sure "studies" is an array of numeric IDs
      const numericStudies = Array.isArray(surveyData.studies)
        ? surveyData.studies.map((s) => parseInt(s, 10))
        : [];

      // Transform the data to match the backend expectations
      const transformedData = {
        name: surveyData.name,
        short_description: surveyData.short_description,
        long_description: surveyData.long_description,
        status: surveyData.status,
        // Keep the original survey_type without transforming it
        survey_type: surveyData.survey_type,
        respondent_type: surveyData.respondent_type || 'participant',
        // Set is_missing based on survey_type consistently
        is_missing: surveyData.survey_type === 'missing_form',
        is_hidden: surveyData.is_hidden,
        rank: surveyData.rank,
        version: surveyData.version || 'A',
        sponsor_id: localStorage.getItem('selectedSponsor'),
        study_name: surveyData.study_name,

        // CRITICAL: pass in numeric study IDs for the "studies" field
        studies: numericStudies,
        adaptive_config: surveyData.adaptive_config,

        questions_data: surveyData.questions.map((q, index) => {
          console.log('Processing question:', q);
          const type = getBackendType(q.type || q.question_type);

          return {
            text: q.text || q.question_text,
            type: type,
            options: formatQuestionOptions(type, q.options || []),
            rank: index + 1,
            is_hidden: false,
            category: q.category,
            slope: q.slope,
            thresholds: q.thresholds
          };
        })
      };

      console.log('Sending transformed data:', JSON.stringify(transformedData, null, 2));

      const response = await api.post('/api/surveys/add', transformedData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API error:', error.response ? error.response.data : error.message);
      throw error;
    }
  },

  getSurveyDetails: async (surveyId) => {
    const response = await api.get(`/api/surveys/${surveyId}`);
    return response.data;
  },

  updateSurvey: async (surveyId, surveyData) => {
    console.log('Updating survey with ID:', surveyId, 'Data:', surveyData);
    try {
      // Make sure "studies" is an array of numeric IDs if provided
      // Preserve the original studies array from surveyData if it exists
      const numericStudies = surveyData.studies || [];
      
      // Parse sponsor_id if it's a string
      let sponsorId = surveyData.sponsor_id;
      try {
        if (typeof sponsorId === 'string') {
          const parsed = JSON.parse(sponsorId);
          sponsorId = parsed.id;
        }
      } catch (e) {
        // If parsing fails, use as is
        console.warn('Failed to parse sponsor_id:', e);
      }

      const transformedData = {
        name: surveyData.name,
        short_description: surveyData.short_description,
        long_description: surveyData.long_description,
        status: surveyData.status,
        // Keep the original survey_type without transforming it
        survey_type: surveyData.survey_type,
        respondent_type: surveyData.respondent_type || 'participant',
        // Set is_missing based on survey_type
        is_missing: surveyData.survey_type === 'missing_form',
        is_hidden: surveyData.is_hidden,
        rank: surveyData.rank,
        version: surveyData.version || 'A',
        sponsor_id: sponsorId,
        study_name: surveyData.study_name,
        studies: numericStudies,
        adaptive_config: surveyData.adaptive_config,
        questions_data: (surveyData.questions || []).map((q, index) => ({
          text: q.text || q.question_text,
          type: getBackendType(q.type || q.question_type),
          options: formatQuestionOptions(q.type, q.options || []),
          rank: index + 1,
          is_hidden: false,
          category: q.category,
          slope: q.slope,
          thresholds: q.thresholds
        }))
      };

      console.log('Transformed survey data:', transformedData);

      const response = await api.put(`/api/surveys/${surveyId}/`, transformedData);
      return response.data;
    } catch (error) {
      console.error('Error updating survey:', error);
      throw error;
    }
  },

  deleteSurvey: async (surveyId) => {
    try {
      await api.delete(`/api/surveys/${surveyId}/`);
    } catch (error) {
      console.error('Error deleting survey:', error);
      throw error;
    }
  },

  getGlobalSurveys: async (type = 'standard') => {
    try {
      const response = await api.get('/api/global-library/surveys', {
        params: { type }
      });
      console.log(`Fetched global ${type} surveys:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching global ${type} surveys:`, error);
      throw error;
    }
  },

  createGlobalSurvey: async (surveyData) => {
    try {
      const transformedData = {
        name: surveyData.name,
        short_description: surveyData.short_description,
        long_description: surveyData.long_description,
        status: surveyData.status || 'active',
        survey_type: surveyData.survey_type || 'standard',  // Ensure survey_type is explicitly set
        respondent_type: surveyData.respondent_type || 'participant',
        is_missing: surveyData.survey_type === 'missing_form',
        is_hidden: surveyData.is_hidden || false,
        version: surveyData.version || '1.0.0',
        sponsor_id: localStorage.getItem('selectedSponsor'),
        is_global: true,
        studies: [],  // Add an empty array for studies to satisfy the serializer
        
        questions_data: surveyData.questions.map((q, index) => ({
          text: q.text || q.question_text,
          type: getBackendType(q.type || q.question_type),
          options: formatQuestionOptions(q.type, q.options || []),
          rank: index + 1,
          is_hidden: false
        }))
      };

      const response = await api.post('/api/global-library/surveys', transformedData);
      return response.data;
    } catch (error) {
      console.error('Error creating global survey:', error);
      throw error;
    }
  },

  updateGlobalSurvey: async (surveyId, surveyData) => {
    try {
      const transformedData = {
        name: surveyData.name,
        short_description: surveyData.short_description,
        long_description: surveyData.long_description,
        status: surveyData.status || 'active',
        survey_type: surveyData.survey_type,
        respondent_type: surveyData.respondent_type || 'participant',
        is_missing: surveyData.survey_type === 'missing_form',
        is_hidden: surveyData.is_hidden || false,
        version: surveyData.version || '1.0.0',
        sponsor_id: localStorage.getItem('selectedSponsor'),
        is_global: true,
        studies: [],  // Add an empty array for studies to satisfy the serializer
        
        questions_data: surveyData.questions.map((q, index) => ({
          text: q.text || q.question_text,
          type: getBackendType(q.type || q.question_type),
          options: formatQuestionOptions(q.type, q.options || []),
          rank: index + 1,
          is_hidden: false
        }))
      };

      const response = await api.put(`/api/global-library/surveys/${surveyId}`, transformedData);
      return response.data;
    } catch (error) {
      console.error('Error updating global survey:', error);
      throw error;
    }
  },

  deleteGlobalSurvey: async (surveyId, forceDelete = false) => {
    try {
      const url = forceDelete 
        ? `/api/global-library/surveys/${surveyId}?force=true`
        : `/api/global-library/surveys/${surveyId}`;
      await api.delete(url);
    } catch (error) {
      console.error('Error deleting global survey:', error);
      throw error;
    }
  },

  importGlobalSurveyToStudy: async (globalSurveyId, studyId) => {
    try {
      const response = await api.post('/api/global-library/import', {
        global_survey_id: globalSurveyId,
        study_id: studyId
      });
      return response.data;
    } catch (error) {
      console.error('Error importing global survey to study:', error);
      throw error;
    }
  },

  getSurveysByStudyId: async (studyId, surveyType = 'standard') => {
    try {
      console.log(`Fetching ${surveyType} surveys for study:`, studyId);
      
      let response;
      if (surveyType === 'all') {
        // Get both standard and missing form surveys
        const [standardResponse, missingResponse] = await Promise.all([
          api.get(`/api/surveys/study/name/${studyId}`, {
            params: { type: 'standard' }
          }),
          api.get(`/api/surveys/study/name/${studyId}`, {
            params: { type: 'missing_form' }
          })
        ]);

        console.log('Standard surveys response:', standardResponse.data);
        console.log('Missing form surveys response:', missingResponse.data);

        // Combine both types of surveys
        const allSurveys = [
          ...(standardResponse.data?.surveys || []),
          ...(missingResponse.data?.surveys || [])
        ];

        console.log('Combined surveys:', allSurveys);
        return { surveys: allSurveys };
      } else {
        // Request only the specified type
        response = await api.get(`/api/surveys/study/name/${studyId}`, {
          params: { type: surveyType }
        });
        
        console.log(`${surveyType} surveys response:`, response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      throw error;
    }
  },

  getLinkedSurveys: async (surveyId) => {
    try {
      const response = await api.get(`/api/surveys/${surveyId}/linked-surveys/`);
      return response.data.linked_surveys || [];
    } catch (error) {
      console.error('Error fetching linked surveys:', error);
      throw error;
    }
  },
  
  updateLinkedSurveys: async (surveyId, linkedSurveyIds) => {
    try {
      // First, get the current survey data to include required fields in the update
      const currentSurvey = await api.get(`/api/surveys/${surveyId}/`);
      
      // Only update the linked_surveys field, but include required fields
      const payload = {
        linked_surveys: linkedSurveyIds,
        // Include the required studies field
        studies: currentSurvey.data.studies || [],
      };
      
      const response = await api.patch(`/api/surveys/${surveyId}/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating linked surveys:', error);
      throw error;
    }
  },
  
  // Queue for batching rank updates
  _rankUpdateQueue: [],
  _processingQueue: false,
  _queueTimer: null,
  _batchSize: 10, // Maximum number of updates to batch in a single request
  _queueDelay: 300, // Milliseconds to wait before processing queue
  
  // Clear the rank update queue and timer
  _clearRankQueue: () => {
    surveyService._rankUpdateQueue = [];
    if (surveyService._queueTimer) {
      clearTimeout(surveyService._queueTimer);
      surveyService._queueTimer = null;
    }
  },
  
  // Process the queued rank updates in batches
  _processRankUpdateQueue: async () => {
    if (surveyService._processingQueue || surveyService._rankUpdateQueue.length === 0) {
      return;
    }
    
    surveyService._processingQueue = true;
    console.log(`Processing ${surveyService._rankUpdateQueue.length} queued rank updates`);
    
    try {
      while (surveyService._rankUpdateQueue.length > 0) {
        // Take a batch from the queue
        const batch = surveyService._rankUpdateQueue.splice(0, surveyService._batchSize);
        console.log(`Processing batch of ${batch.length} rank updates`);
        
        // Create the batch update payload
        const updates = batch.map(item => ({
          survey_id: item.surveyId,
          rank: item.rank
        }));
        
        // Use the batch update endpoint
        await api.post('/api/surveys/batch-update-ranks/', { updates });
        
        // Small delay between batches to avoid overwhelming the server
        if (surveyService._rankUpdateQueue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    } catch (error) {
      console.error('Error processing rank update batch:', error);
    } finally {
      surveyService._processingQueue = false;
    }
  },
  
  // Queue a rank update to be sent in the next batch
  queueRankUpdate: (surveyId, rank) => {
    // Add to queue
    surveyService._rankUpdateQueue.push({ surveyId, rank });
    console.log(`Queued rank update for survey ${surveyId} to rank ${rank}. Queue size: ${surveyService._rankUpdateQueue.length}`);
    
    // Reset the timer
    if (surveyService._queueTimer) {
      clearTimeout(surveyService._queueTimer);
    }
    
    // Set a timer to process the queue
    surveyService._queueTimer = setTimeout(() => {
      surveyService._processRankUpdateQueue();
    }, surveyService._queueDelay);
    
    // If the queue gets too large, process it immediately
    if (surveyService._rankUpdateQueue.length >= surveyService._batchSize * 2) {
      clearTimeout(surveyService._queueTimer);
      surveyService._processRankUpdateQueue();
    }
  },
  
  // Original method - now uses debouncing with lodash if available, or falls back to direct API call
  updateSurveyRank: async (surveyId, rank) => {
    try {
      console.log(`Updating survey ${surveyId} with rank ${rank}`);
      
      // Check if we're in a batch operation (multiple updates happening simultaneously)
      // If we are, queue this update instead of sending it immediately
      if (document.updateMultipleRanks || document.batchOperation) {
        // Use the queue mechanism to batch requests
        surveyService.queueRankUpdate(surveyId, rank);
        return { status: 'queued', message: 'Rank update queued for batch processing' };
      }
      
      // Direct call for individual updates
      const response = await api.patch(`/api/surveys/${surveyId}/update-rank/`, { rank });
      return response.data;
    } catch (error) {
      console.error(`Error updating rank for survey ${surveyId}:`, error);
      throw error;
    }
  },

  updateSurveysLinkedStatus: async (surveyIds, isLinked) => {
    try {
      // Always determine link status based on number of surveys
      // If more than one survey, set to true, otherwise false
      const shouldBeLinked = surveyIds.length > 1;
      
      console.log(`[SurveyLinking] Updating linked status for ${surveyIds.length} surveys: ${JSON.stringify(surveyIds)}`);
      console.log(`[SurveyLinking] Setting is_linked=${shouldBeLinked} (determined by number of surveys)`);
      
      const response = await api.post('/api/surveys/update_linked_status/', {
        survey_ids: surveyIds,
        is_linked: shouldBeLinked
      });
      console.log(`[SurveyLinking] Successfully updated linked status. Response:`, response.data);
      return response.data;
    } catch (error) {
      console.error('[SurveyLinking] Error updating survey linked status:', error);
      throw error;
    }
  },

  /**
   * Set who completes a survey: 'participant' (PRO), 'caregiver' (ObsRO), or
   * 'clinician' (ClinRO). Lightweight PATCH so it can be set inline from the
   * Schedule Builder without resending the full survey payload.
   */
  updateSurveyRespondentType: async (surveyId, respondentType) => {
    const response = await api.patch(`/api/surveys/${surveyId}/`, {
      respondent_type: respondentType,
    });
    return response.data;
  },

  // Survey Assignment Methods
  getMyAssignments: async (bypassCache = true, date) => {
    // Always fetch fresh data by default
    const params = {
        'bypass_cache': bypassCache,
        'date': date
    };

    const response = await api.get('/api/survey-assignments/my_assignments/', { params });
    return response.data;
  },

  // FECOA-1226: today's completed survey records for the signed-in participant.
  // Patient-prompted windows reset themselves to OPEN after each completion,
  // so the dashboard "Completed" count and "Today's Completed" filter must
  // read from CompletedSurvey rather than SurveyAssignment.status.
  getMyCompletedToday: async (date) => {
    const params = { date };
    const response = await api.get('/api/survey-assignments/my_completed_today/', { params });
    return response.data;
  },

  updateAssignmentStatus: async (assignmentId, status) => {
    const response = await api.post(
      `/api/survey-assignments/${assignmentId}/update_status/`,
      { status }
    );
    return response.data;
  },

  // Load all screens for an assignment in one request (used by fast-nav mode)
  loadAllScreens: async (assignmentId) => {
    const response = await api.post('/api/survey-participant-responses/load_all_screens/', {
      assignment_id: assignmentId,
    });
    return response.data;
  },

  // Lightweight validity check — called on a polling interval during survey taking
  checkSessionValid: async (assignmentId) => {
    const response = await api.post('/api/survey-participant-responses/check_session_valid/', {
      assignment_id: assignmentId,
    });
    return response.data;
  },

  // Survey Response Methods
  getNextQuestion: async (assignmentId, statusList, currentScreenId = null, targetScreenId = null) => {
    const payload = { 
      assignment_id: assignmentId, 
      status_list: statusList 
    };
    // Include current_screen_id for Assessment Builder screen-based navigation
    if (currentScreenId) {
      payload.current_screen_id = currentScreenId;
    }
    // Include target_screen_id for skip logic navigation (jump directly to a specific screen)
    if (targetScreenId) {
      payload.target_screen_id = targetScreenId;
    }
    console.log('getNextQuestion API call with payload:', payload);
    const response = await api.post(`/api/survey-participant-responses/get_next_question/`, payload);
    return response.data;
  },

  getPreviousQuestion: async (assignmentId, currentQuestionId, statusList) => {
    const response = await api.post(`/api/survey-participant-responses/get_previous_question/`, 
      { assignment_id: assignmentId, current_question_id: currentQuestionId, status_list: statusList }
   );
   return response.data;
  },

  saveResponse: async (assignmentId, questionId, responseData, statusList) => {
    const response = await api.post('/api/survey-participant-responses/save_response/', {
      assignment_id: assignmentId,
      question_id: questionId,
      response_data: responseData, 
      status_list: statusList
    });
    return response.data;
  },

  /**
   * FECOA-1965: record that the participant manually exited an assessment
   * before completing it. Audit-only — the assignment stays in progress and
   * resumable, so nothing about the survey state changes here.
   */
  exitAssessment: async (assignmentId, { screen_id = null, exited_at = null } = {}) => {
    const response = await api.post('/api/survey-participant-responses/exit_assessment/', {
      assignment_id: assignmentId,
      screen_id,
      exited_at,
    });
    return response.data;
  },

  submitSurvey: async (assignmentId, extra = {}) => {
    // `extra` may include:
    //   - skipped_questions: [{question_id, skipped_by_rule_id}] — list of
    //     questions legitimately bypassed by skip-logic during the session.
    //     The backend pairs this with its own visited-set check so required
    //     questions hidden by a rule don't block submission.
    const response = await api.post('/api/survey-participant-responses/submit_survey/', {
      assignment_id: assignmentId,
      ...extra,
    });
    return response.data;
  },

  getNextLinkedAssignment: async (assignmentId) => {
    try {
      const response = await api.get(`/api/survey-assignments/${assignmentId}/next_linked_assignment/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching next linked assignment:', error);
      // Return null if there's no next linked assignment
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getSurveyProgress: async (assignmentId) => {
    return api.get(`/api/survey-participant-responses/progress/`, {
      params: { assignment_id: assignmentId }
    })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching survey progress:', error);
        throw error;
      });
  },

  getQuestionResponse: async (assignmentId, questionId) => {
    return api.get(`/api/survey-participant-responses/assignments/${assignmentId}/responses/${questionId}/`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching question response:', error);
        throw error;
      });
  },
  
  getNextSequentialQuestion: async (assignmentId, currentQuestionId) => {
    return api.get(`/api/survey-participant-responses/assignments/${assignmentId}/next_sequential_question/`, {
      params: { 
        current_question_id: currentQuestionId
      }
    })
    .then(response => response.data)
    .catch(error => {
      console.error('Error fetching next sequential question:', error);
      throw error;
    });
  },

  /**
   * Get active survey windows for a participant
   * This returns all windows that are available (open or in_progress) for the participant
   * @param {string} participantId - The participant ID
   * @returns {Promise<Object>} - The API response with active windows
   */
  getActiveSurveyWindows: async (participantId) => {
    try {
      console.log(`[SurveyService] Fetching active survey windows for participant ${participantId}`);
      // This endpoint uses AllWindowsForActivationView which is correctly implemented in the backend
      const response = await api.get(`/api/participants/${participantId}/all-surveys-for-activation/`);
      console.log(`[SurveyService] Received ${response.data.length || 0} active windows`);
      return response.data;
    } catch (error) {
      console.error('[SurveyService] Error fetching active survey windows:', error);
      throw error;
    }
  },

  /**
   * Get all survey windows with pagination and optional filtering
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Items per page (default: 50)
   * @param {string} options.status - Filter by status
   * @param {string} options.search - Search term
   * @param {string} options.studyId - Filter by study ID
   * @param {string} options.participantId - Filter by participant ID
   * @param {string} options.surveyId - Filter by survey ID
   * @returns {Object} Paginated windows data with metadata
   */
  getAllSurveyWindows: async (options = {}) => {
    try {
      const {
        page = 1,
        pageSize = 50,
        status = null,
        startDate = null,
        endDate = null,
        search = null,
        participantId = null,
      } = options;

      // Build the query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      
      if (status) params.append('status', status);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (search) params.append('search', search);
      if (participantId) params.append('participant_id', participantId);

      // Enhance authentication by ensuring token is included
      const headers = {
        'Authorization': `Token ${localStorage.getItem('token')}` 
      };

      // Make API call with enhanced auth headers
      const response = await api.get(`/api/survey-windows/detailed/?${params.toString()}`, { headers });
      
      // Handle paginated response structure
      const pagination = {
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
        currentPage: page,
        totalPages: Math.ceil((response.data.count || 0) / pageSize),
      };
      
      // Extract results array
      const windowsArray = response.data.results || [];
      
      // Format the data for display in a table
      const formattedWindows = windowsArray.map(window => {
        // Get the real survey name if available
        let surveyName = window.survey_name || 'Unknown Survey';
        const surveyId = window.survey_id;
        
        // Enhanced survey name handling
        // First check for rich survey name that includes status and other details
        if (window.survey_description) {
          // Add description as tooltip or additional info
          window.survey_tooltip = window.survey_description;
        }
        
        // If the survey name is just a placeholder with the ID, try to get a real name
        // from our survey cache or other sources if available
        if (surveyName === `Survey #${surveyId}` && window.survey_title) {
          surveyName = window.survey_title;
        }
        
        // Check for survey detailed type
        if (window.survey_detailed_type) {
          // Add as a tag or badge in the UI
          window.survey_type_display = window.survey_detailed_type;
        }
        
        return {
          id: window.id || 'N/A',
          surveyId: surveyId,
          surveyName: surveyName,
          participantId: window.participant_id,
          participantName: window.participant_name || 'Unknown Participant',
          studyId: window.study_id,
          studyName: window.study_name || 'Unknown Study',
          completionDate: window.completion_time ? new Date(window.completion_time).toLocaleDateString() : 'Not completed',
          status: window.status || 'Unknown',
          startDate: window.start_time ? new Date(window.start_time).toLocaleDateString() : 'Not set',
          endDate: window.end_time ? new Date(window.end_time).toLocaleDateString() : 'Not set',
          isActivated: window.is_activated !== undefined ? window.is_activated : false
        };
      });
      
      // Return both the formatted data and pagination info
      return {
        windows: formattedWindows,
        pagination
      };
      
    } catch (error) {
      console.error('Error fetching survey windows:', error);
      // Enhanced error handling with specific messages based on status codes
      if (error.response) {
        const { status } = error.response;
        
        if (status === 403) {
          // Handle authentication/permission issues
          console.error('Authentication error: Not authorized to view survey windows');
          // Check if token is invalid
          if (localStorage.getItem('token')) {
            // Try to refresh token or redirect to login
            console.log('Attempting to refresh authentication token...');
          }
          throw new Error('Not authorized to view survey windows. Please log in again.');
        } else if (status === 404) {
          throw new Error('Survey windows endpoint not found. Please contact support.');
        } else if (status >= 500) {
          throw new Error('Server error occurred. Please try again later.');
        }
      }
      
      // Generic fallback error
      throw new Error('Unable to fetch survey windows data');
    }
  },

};

export default surveyService;
